import { VendorPickerBC } from '@/components/filtros/VendorPickerBC';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useActualizarProveedor, useCrearProveedor, useProveedores } from '@/hooks/useFlujo';
import { useVendoresBC } from '@/hooks/useVendoresBC';
import { bancoDeIban } from '@adelante/shared';
import { UploadSimple } from '@phosphor-icons/react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

/** Nombre del banco según el código del IBAN (antes se fijaba 'BAC' aunque la cuenta fuera de otro banco). */
function nombreBanco(iban: string): string | null {
  const banco = bancoDeIban(iban);
  return banco === 'BAC' ? 'BAC' : banco === 'BN' ? 'Banco Nacional' : null;
}

interface FilaImportar {
  nombreBAC: string;
  cuentaBancaria: string;
  cedula: string | null;
  vendorCode: string | null;
  incluir: boolean;
}

/** Parsea una línea de CSV respetando comillas (para que una coma dentro de un campo no lo corte). */
function parsearLineaCsv(linea: string): string[] {
  const celdas: string[] = [];
  let actual = '';
  let dentroDeComillas = false;
  for (let i = 0; i < linea.length; i++) {
    const c = linea[i];
    if (c === '"') {
      dentroDeComillas = !dentroDeComillas;
    } else if (c === ',' && !dentroDeComillas) {
      celdas.push(actual.trim());
      actual = '';
    } else {
      actual += c;
    }
  }
  celdas.push(actual.trim());
  return celdas;
}

/**
 * El CSV que exporta BAC (Consulta de Referencias) trae 2-3 líneas de metadata antes del
 * encabezado real (título del plan/cuenta, fecha de la consulta, línea en blanco). Se busca la
 * fila cuya primera celda sea literalmente "Referencia" para ubicar el encabezado real, sin
 * asumir un número fijo de líneas previas.
 */
function parsearCsvBAC(texto: string): { encabezado: string[]; filas: string[][] } {
  const lineas = texto.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const indiceEncabezado = lineas.findIndex(
    (l) => parsearLineaCsv(l)[0]?.toLowerCase() === 'referencia',
  );
  if (indiceEncabezado === -1) return { encabezado: [], filas: [] };
  const encabezado = parsearLineaCsv(lineas[indiceEncabezado] ?? '').map((h) => h.toLowerCase());
  const filas = lineas.slice(indiceEncabezado + 1).map(parsearLineaCsv);
  return { encabezado, filas };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ImportarReferenciasBACDialog({ open, onClose }: Props) {
  const { data: proveedores } = useProveedores();
  const { data: vendorsData } = useVendoresBC();
  const crear = useCrearProveedor();
  const actualizar = useActualizarProveedor();
  const inputRef = useRef<HTMLInputElement>(null);

  const [filas, setFilas] = useState<FilaImportar[]>([]);
  const [importando, setImportando] = useState(false);

  const vendors = vendorsData?.data ?? [];

  function sugerirVendorCode(nombreBAC: string): string | null {
    const norm = nombreBAC.trim().toLowerCase();
    if (!norm) return null;
    const match = vendors.find((v) => {
      const nombreVendor = (v.vendorName ?? '').trim().toLowerCase();
      if (!nombreVendor) return false;
      return nombreVendor.includes(norm) || norm.includes(nombreVendor);
    });
    return match?.vendorCode ?? null;
  }

  function manejarArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    const lector = new FileReader();
    lector.onload = () => {
      const texto = String(lector.result ?? '');
      const { encabezado, filas: datos } = parsearCsvBAC(texto);
      if (encabezado.length === 0) {
        toast.error(
          'No se encontró el encabezado "Referencia" en el archivo — ¿es el CSV correcto?',
        );
        return;
      }
      const idxCuentaBancaria = encabezado.findIndex(
        (h) => h.includes('cta') || h.includes('cuenta banc'),
      );
      const idxNombre = encabezado.findIndex((h) => h.includes('nombre'));
      const idxIdentificacion = encabezado.findIndex(
        (h) => h === 'identificación' || h === 'identificacion',
      );

      const nuevas: FilaImportar[] = datos
        .filter((cols) => cols[idxCuentaBancaria] && cols[idxNombre])
        .map((cols) => {
          const identificacion =
            idxIdentificacion >= 0 ? (cols[idxIdentificacion] ?? '').trim() : '';
          return {
            cuentaBancaria: cols[idxCuentaBancaria] ?? '',
            nombreBAC: cols[idxNombre] ?? '',
            cedula: identificacion || null,
            vendorCode: sugerirVendorCode(cols[idxNombre] ?? ''),
            incluir: true,
          };
        });
      setFilas(nuevas);
    };
    lector.readAsText(archivo);
    e.target.value = '';
  }

  const cantidadAImportar = useMemo(
    () => filas.filter((f) => f.incluir && f.vendorCode).length,
    [filas],
  );

  async function confirmarImportacion() {
    setImportando(true);
    let ok = 0;
    let errores = 0;
    // El CSV real de BAC trae varias referencias históricas para el mismo proveedor (mismo
    // vendorCode en varias filas) — hay que actualizar el mismo registro en cada una, no crear
    // uno nuevo por fila. `proveedores` es la lista de ANTES de empezar a importar, así que se
    // completa a medida que se van creando proveedores dentro de este mismo lote.
    const idPorVendorCode = new Map<string, number>();
    for (const p of proveedores ?? []) {
      if (p.vendorCode) idPorVendorCode.set(p.vendorCode, p.id);
    }
    for (const fila of filas) {
      if (!fila.incluir || !fila.vendorCode) continue;
      const idExistente = idPorVendorCode.get(fila.vendorCode);
      const vendorBC = vendors.find((v) => v.vendorCode === fila.vendorCode);
      try {
        if (idExistente) {
          await actualizar.mutateAsync({
            id: idExistente,
            agregarCuenta: {
              moneda: 'C',
              iban: fila.cuentaBancaria,
              banco: nombreBanco(fila.cuentaBancaria),
            },
            cedula: fila.cedula,
          });
        } else {
          const resultado = await crear.mutateAsync({
            nombre: vendorBC?.vendorName ?? fila.nombreBAC,
            vendorCode: fila.vendorCode,
            cuentas: [
              {
                moneda: 'C',
                iban: fila.cuentaBancaria,
                banco: nombreBanco(fila.cuentaBancaria),
                predeterminada: true,
              },
            ],
            cedula: fila.cedula,
            notas: null,
          });
          if (resultado.data) idPorVendorCode.set(fila.vendorCode, resultado.data.id);
        }
        ok++;
      } catch {
        errores++;
      }
    }
    setImportando(false);
    if (ok > 0) toast.success(`${ok} proveedor(es) actualizados con su IBAN.`);
    if (errores > 0) toast.error(`${errores} fila(s) fallaron al importar.`);
    setFilas([]);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} modal={false}>
      <DialogContent className="max-w-3xl" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Importar referencias de BAC</DialogTitle>
          <DialogDescription>
            Subí el CSV que exporta BAC desde Consulta de Referencias (Planillas y Proveedores →
            Consultar Envíos), vinculá cada fila con su proveedor de BC y confirmá cuáles importar —
            Contabilidad avisó que muchas referencias viejas ya no se usan.
          </DialogDescription>
        </DialogHeader>

        {filas.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-8">
            <UploadSimple className="h-8 w-8 text-muted-foreground" weight="bold" />
            <Button variant="outline" onClick={() => inputRef.current?.click()}>
              Elegir archivo CSV
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={manejarArchivo}
            />
          </div>
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-auto">
            <p className="text-xs text-muted-foreground">{filas.length} fila(s) encontradas.</p>
            {filas.map((fila, i) => (
              <div
                key={`${fila.cuentaBancaria}-${i}`}
                className="flex items-center gap-3 rounded-md border border-border p-2"
              >
                <input
                  type="checkbox"
                  checked={fila.incluir}
                  onChange={() =>
                    setFilas((actual) =>
                      actual.map((f, idx) => (idx === i ? { ...f, incluir: !f.incluir } : f)),
                    )
                  }
                  className="h-4 w-4 accent-adelante-primary"
                />
                <div className="w-[240px] shrink-0">
                  <p className="truncate text-sm font-medium">{fila.nombreBAC}</p>
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    {fila.cuentaBancaria}
                    {fila.cedula ? ` · ${fila.cedula}` : ''}
                  </p>
                </div>
                <VendorPickerBC
                  enLinea
                  value={fila.vendorCode}
                  onChange={(vendorCode) =>
                    setFilas((actual) =>
                      actual.map((f, idx) => (idx === i ? { ...f, vendorCode } : f)),
                    )
                  }
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {filas.length > 0 && (
            <Button variant="outline" onClick={() => setFilas([])}>
              Elegir otro archivo
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          {filas.length > 0 && (
            <Button disabled={cantidadAImportar === 0 || importando} onClick={confirmarImportacion}>
              {importando ? 'Importando...' : `Importar ${cantidadAImportar} proveedor(es)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
